import React from 'react';

import { cn } from '~/lib/utils';

import { FilterButton } from './components/FilterButton';
import { SearchInput } from './components/SearchInput';
import { SortButton } from './components/SortButton';
import { useSearchBarState } from './hooks/useSearchBarState';
import { SEARCH_BAR_CONFIG, SEARCH_BAR_STYLES } from './constants';
import type { SearchBarProps } from './types';

const SearchBar: React.FC<SearchBarProps> = ({
  placeholder = SEARCH_BAR_CONFIG.PLACEHOLDER,
  value = '',
  onChange,
  onSearch,
  onClear,
  disabled = false,
  className,
  debounceMs = SEARCH_BAR_CONFIG.DEFAULT_DEBOUNCE_MS,
  searchEmptyImmediately = true,
  showClearButton = true,
  autoFocus = false,
  buttonText = 'Sort by',
  onSortSelect,
  selectedSortValue,
  sortOptions,
  showSortButton = true,
  showFilterButton = false,
  showBorder = true,
  onFilterClick,
  filterButtonText = 'Filters',
  rightActions
}) => {
  const {
    internalValue,
    isFocused,
    inputRef,
    handleInputChange,
    handleInputFocus,
    handleInputBlur,
    handleKeyDown,
    handleClear
  } = useSearchBarState({
    value,
    onChange,
    onSearch,
    onClear,
    debounceMs,
    searchEmptyImmediately,
    autoFocus
  });

  return (
    <div
      className={cn(
        showBorder && SEARCH_BAR_STYLES.CONTAINER,
        isFocused && SEARCH_BAR_STYLES.CONTAINER_FOCUSED,
        disabled && SEARCH_BAR_STYLES.CONTAINER_DISABLED,
        className
      )}>
      <SearchInput
        ref={inputRef}
        value={internalValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onClear={handleClear}
        placeholder={placeholder}
        disabled={disabled}
        showClearButton={showClearButton}
        isFocused={isFocused}
        autoFocus={autoFocus}
      />

      {showSortButton && (
        <SortButton
          text={buttonText}
          disabled={disabled}
          onSortSelect={onSortSelect}
          selectedSortValue={selectedSortValue}
          options={sortOptions}
        />
      )}

      {showFilterButton && (
        <FilterButton
          text={filterButtonText}
          onClick={onFilterClick}
          disabled={disabled}
        />
      )}

      {rightActions != null && (
        <div
          className={cn(SEARCH_BAR_STYLES.BUTTON_CONTAINER, 'relative')}
          data-testid="search-bar-right-actions">
          {rightActions}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
